<template>
    <div class="apr-product-card" :class="`apr-product-card--${productType}`" :data-product-id="productId">
        <div class="apr-product-card__image">
            <LazyClientOnly>
                <NuxtImg
                    class="image"
                    :src="productImage"
                    alt="Product Logo"
                    loading="lazy"
                    :style="{
                        'object-fit': productType === 'hotel' ? 'cover' : 'contain',
                        width: '100%',
                        height: '100%',
                    }"
                    placeholder
                    placeholder-class="no-image"
                />
            </LazyClientOnly>
        </div>
        <div class="apr-product-card__content">
            <div class="apr-product-card__info">
                <a class="apr-product-card__title" :href="productLink" external>
                    {{ productName }}
                </a>
                <div v-if="productRating > 0" class="rating">
                    <uiStarsRating :value="productStarsRating" />
                    <div class="rating-info">
                        <span v-if="productRating > 4.25" class="rating-word">Excellent</span>
                        <span v-else-if="productRating > 3.75" class="rating-word">Great</span>
                        <span v-else-if="productRating > 3.25" class="rating-word">Good</span>
                        <span v-else-if="productRating === 3.25" class="rating-word">Average</span>
                        <span v-else class="rating-word"></span>
                        <span class="review-count">({{ productRatingCount }} Reviews)</span>
                    </div>
                </div>
                <div v-if="productDistance && airportCode" class="apr-product-card__info-item">
                    <i data-icon="true" :class="airportTypeIcon" class="distance-icon"></i>
                    {{ productDistance }} miles from {{ airportCode }}
                </div>
                <div v-if="productType === 'hotel'" class="apr-product-card__info-item">
                    <i data-icon="true" class="apr-icon-price-tag-3-line"></i
                    >{{ $t('aprPackagesDeal.packageDealStatus') }}
                </div>
                <div
                    v-if="productShuttleFreeOnly && (productShuttleOption === 'Free Shuttle' || productShuttleFree)"
                    class="apr-product-card__info-item"
                >
                    <i data-icon="true" class="apr-icon-bus-2-line"></i>
                    {{ $t('freeShuttle') }}
                </div>
                <div v-if="!productShuttleFreeOnly && productShuttleOption" class="apr-product-card__info-item">
                    <i data-icon="true" :class="shuttleOption.icon"></i>
                    <div>
                        {{ shuttleOption.text }}
                    </div>
                </div>
                <div v-if="freeCancellation" class="apr-product-card__info-item">
                    <i data-icon="true" class="apr-icon-check-line"></i>
                    <div>
                        {{ $t('freeCancellation') }}
                    </div>
                </div>
                <template v-if="cardType === 'ParkingLotProductCard'">
                    <div class="apr-product-card__info-item">
                        <i data-icon="true" class="apr-icon-time"></i>
                        <div>
                            {{ $t('open') }}
                        </div>
                    </div>
                </template>
                <div v-if="productType === 'parking' && productOptions?.length" class="apr-product-card__info-item">
                    <i data-icon="true" class="apr-icon-parking-box-line"></i>
                    <span>
                        {{ $t('parkingOptions') }}:
                        <span v-for="(option, index) in productOptions" :key="option">
                            {{ option }}{{ index === productOptions.length - 1 ? '' : ', ' }}
                        </span>
                    </span>
                </div>
            </div>
            <div class="apr-product-card__buttons">
                <UiAdvancedBtn
                    component-type="link"
                    component-variant="tertiary"
                    :with-arrow="true"
                    :to="productLink"
                    external
                >
                    View rates
                </UiAdvancedBtn>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
const props = defineProps<{
    productId?: string | number;
    productType?: 'parking' | 'hotel';
    productImage: string;
    productLink: any;
    productName: any;
    productRating: any;
    productStarsRating: any;
    productRatingCount: any;
    productDistance?: any;
    airportCode?: string;
    productShuttleOption?: string;
    productShuttleFreeOnly?: boolean;
    productShuttleFree?: boolean;
    productOptions?: any;
    productLocationType?: 'port' | 'airport';
    cardType: string;
    freeCancellation?: boolean;
}>();

const { t } = useI18n();

const airportTypeIcon = props.productLocationType === 'port' ? 'apr-icon-ship-fill' : 'apr-icon-plane-line';

const shuttleOption = computed<{ text: string; icon: string }>(() => {
    if (props.productShuttleOption === 'Free Shuttle') {
        return { icon: 'apr-icon-bus-2-line', text: t('freeShuttle') };
    } else if (props.productShuttleOption === 'Free Ride Share') {
        return { icon: 'apr-icon-bus-2-line', text: t('freeRideShare') };
    } else if (props.productShuttleOption === 'Free Taxi') {
        return { icon: 'apr-icon-bus-2-line', text: t('freeTaxi') };
    } else if (props.productShuttleOption === 'Walk to Airport') {
        return { icon: 'apr-icon-walk-line', text: t('walkToAirport') };
    } else if (props.productShuttleOption === 'Metro to Airport') {
        return { icon: 'apr-icon-train-line', text: t('metroToAirport') };
    } else {
        return { icon: 'apr-icon-parking-box-line', text: t('thirdPartyShuttle') };
    }
});
</script>

<style lang="postcss" scoped>
.apr-product-card {
    @apply apr-card overflow-hidden flex flex-col;
    word-wrap: break-word;

    &__image {
        img {
            @apply !h-[200px] object-cover;
            &.no-image {
                @apply bg-text-secondary-grey;
            }
        }
    }

    &--parking {
        .apr-product-card__image {
            @apply px-6 py-4 border-b border-solid border-b-neutral-soft-grey;
            img {
                @apply !h-[112px];
            }
        }
    }

    &__content {
        @apply p-6 grow flex flex-col;
    }

    &__info {
        @apply flex flex-col items-start gap-2 mb-auto;
    }

    &__title {
        @apply font-semibold text-base md:text-xl text-text-primary-asphalt text-left hover:underline;
    }

    .rating {
        @apply flex flex-wrap items-center gap-2 mb-2;
    }
    &__info-item {
        @apply flex justify-start text-left;
        [data-icon='true'] {
            @apply shrink-0 mr-2;
        }
    }
    &__buttons {
        @apply mt-4 flex justify-between;
        .btn-brand {
            @apply ml-auto -mr-6;
        }
    }

    .rating-info {
        @apply text-base font-semibold flex gap-1;
    }
}
</style>
